import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Paper,
  styled,
  Switch,
  Theme,
  Typography,
} from '@material-ui/core';

import { useTranslation } from '../../../../../hooks/useTranslation';
import colors from '../../../../../themes/main-colors';
import { useIsMobile } from '../../../../../util/style-utils';
import ExclamationIcon from '../../../../../static/exclamationIcon.svg';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Shadow } from '../../../../../constants';
import useAuth from '../../../../../hooks/useAuth';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    boxWrapper: {
      marginTop: 10,
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
      padding: theme.spacing(2.5, 3, 2.75, 3),
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        boxShadow: 'none',
        marginTop: 10,
        borderBottom: `1px solid ${colors.lightGray1}`,
      },
      backgroundColor: colors.white,
    },
    buttonBox: {
      flexDirection: 'row-reverse',
      justifyContent: 'flex-end',
      alignContent: 'center',
      alignItems: 'center',
    },
    backButton: {
      padding: theme.spacing(1.125, 1.875),
      float: 'left',
      cursor: 'pointer',
      color: colors.brightBlue,
      '&:hover': {
        textDecoration: 'underline',
      },
      [theme.breakpoints.down('xs')]: {
        float: 'none',
        textAlign: 'center',
      },
      '& h4': {
        textDecoration: 'underline',
      },
    },
    submitButtonWrapper: {
      float: 'right',
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(2, 0, 1, 0),
        textAlign: 'center',
        float: 'none',
      },
    },
    resetBreakerButton: {
      padding: theme.spacing(1.625),
      backgroundColor: colors.sparkBlue,
      borderRadius: 5,
      color: colors.white,
      fontSize: '1.125rem',
      letterSpacing: 0.36,
      lineHeight: '1.125rem',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        borderRadius: 50,
      },
    },
    reportOutageButton: {
      padding: theme.spacing(1.625),
      backgroundColor: colors.white,
      border: `1px solid ${colors.sparkBlue}`,
      borderRadius: 5,
      color: colors.sparkBlue,
      fontSize: '1.125rem',
      letterSpacing: 0.36,
      lineHeight: '1.125rem',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        borderRadius: 50,
      },
    },
    outageButtontext: {
      letterSpacing: 2,
    },
    divider: {
      marginTop: '1rem',
      marginBottom: '1rem',
    },
    bodySection: {
      fontSize: '1rem',
      lineHeight: '1.125rem',
    },
    cancelButton: {
      color: colors.brightBlue,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    textToBold: {
      fontWeight: 'bold',
    },
    accordionDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    gridDetails: {
      boxShadow: Shadow.NONE,
      background: colors.lightGray6,
    },
    dividerDetails: {
      padding: '1px',
      backgroundColor: colors.white,
      marginBottom: '15px',
    },
    grid2Details: {
      marginTop: '2px',
    },
    circuitBreakerAccordion: {
      background: colors.lightGray6,
      padding: '0.5rem',
      boxShadow: Shadow.NONE,
    },
    restoringPower: {
      fontSize: '14px',
      marginBottom: '15px',
    },
    switchDetails: {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: '7px',
    },
    textMarginBottom: {
      marginBottom: '15px',
    },
    switchBoxDetails: {
      backgroundColor: colors.lightGray6,
      width: '100%',
      display: 'inline-flex',
      padding: '10px 5px',
      marginTop: '15px',
      flexDirection: 'column',
    },
    switchOptions: {
      margin: '0px 9px',
    },
    resetBreakerDetails: {
      paddingLeft: '1rem',
    },
  }),
);

export interface Props {
  onClose: (e?: any, isMultiPremise?: boolean) => void;
  onReset: () => void;
  onProceed: () => void;
  name?: string;
  onPrevious: () => void;
}

export default function OutageLocationCheckBreakerPopup(props: Props) {
  const classes = useStyles();
  const { t, richT } = useTranslation();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();

  const Item = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
  }));

  const customerCarePortland = t('CUSTOMER_CARE_NUMBER_PORTLAND');
  const customerCareNumber = t('CUSTOMER_CARE_NUMBER_OTHER');
  const OutageToggleSwitch = styled(Switch)(({ theme }) => ({
    width: 30,
    height: 18,
    padding: 0,
    margin: '5px 10px',
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: 15,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: 'translateX(9px)',
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(12px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: 'white',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 14,
      height: 14,
      borderRadius: 6,
      backgroundColor: colors.sparkBlue,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
    },
    '& .MuiSwitch-track': {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: 'white',
      border: '1px solid gray',
      boxSizing: 'border-box',
    },
  }));

  return (
    <form>
      {isMobile && (
        <Grid className={classes.bodySection}>
          <ExclamationIcon style={{ width: '100%', marginBottom: '1rem' }} />
          <Typography variant={'subtitle1'} className={classes.restoringPower}>
            {richT('SOMETIMES_RESTORING_POWER_CAN_BE_SIMPLE')}
          </Typography>
          <Typography
            variant={'subtitle1'}
            className={classes.textMarginBottom}
          >
            {t('IF_YOU_ARE_UNSURE_OF_RESETTING')}
          </Typography>
          <Typography
            variant={'subtitle1'}
            className={classes.textMarginBottom}
          >
            {t('UNABLE_TO_LOCATE_BREAKER_OR_MAIN_BREAKER')}{' '}
            {
              <a
                href={'tel:' + customerCarePortland}
                style={{
                  textDecoration: 'underline',
                  color: colors.brightBlue,
                }}
              >
                {' '}
                {customerCarePortland}
              </a>
            }{' '}
            {t('OR')}{' '}
            {
              <a
                href={'tel:' + customerCareNumber}
                style={{
                  textDecoration: 'underline',
                  color: colors.brightBlue,
                }}
              >
                {' '}
                {customerCareNumber}
              </a>
            }{' '}
            {t('TO_SPEAK_WITH_AN_ADVISOR')}
          </Typography>

          <Accordion className={classes.circuitBreakerAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant={'subtitle2'}>
                {t('HOW_TO_FIND_YOUR_BREAKER')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Typography
                variant={'subtitle1'}
                className={classes.textMarginBottom}
              >
                {richT('HOW_TO_FIND_BREAKER_CONTENT')}
              </Typography>
              <Typography
                variant={'subtitle2'}
                className={classes.textMarginBottom}
              >
                {t('LOCATING_YOUR_BREAKER_BOX')}
              </Typography>
              <Divider variant="middle" className={classes.dividerDetails} />

              <Grid container spacing={2} className={classes.grid2Details}>
                <Grid xs={12}>
                  <Item className={classes.gridDetails}>
                    <img
                      style={{
                        maxWidth: '80%',
                        maxHeight: '80%',
                      }}
                      src={require('../../../../../images/InsideBreaker.png')}
                      alt="Check Inside Breaker Details"
                    />
                  </Item>
                </Grid>

                <Grid xs={12}>
                  <Item className={classes.gridDetails}>
                    <Typography variant={'subtitle2'}>
                      {t('IF_YOU_DONT_HAVE_OUTSIDE_BREAKER')}
                    </Typography>
                    <Typography variant={'subtitle2'}>
                      {t('STEPS_TO_RESET_INSIDE_BREAKERS')}
                    </Typography>
                    <ol>
                      <li className={classes.textToBold}>
                        <Typography variant={'subtitle1'}>
                          {t('TURN_OFF_THE_MAIN_BREAKER')}
                        </Typography>
                      </li>
                      <li className={classes.textToBold}>
                        <Typography variant={'subtitle1'}>
                          {t('TURN_OFF_THE_SMALLER_BREAKERS')}
                        </Typography>
                      </li>
                      <li className={classes.textToBold}>
                        <Typography variant={'subtitle1'}>
                          {t('TURN_ON_THE_MAIN_BREAKER')}
                        </Typography>
                      </li>
                      <li className={classes.textToBold}>
                        <Typography variant={'subtitle1'}>
                          {t('TURN_ON_THE_SMALLER_BREAKERS')}
                        </Typography>
                      </li>
                    </ol>
                  </Item>
                </Grid>
              </Grid>

              <Divider variant="middle" className={classes.dividerDetails} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Item className={classes.gridDetails}>
                    <img
                      className=""
                      style={{
                        maxWidth: '60%',
                        maxHeight: '60%',
                      }}
                      src={require('../../../../../images/OutsideBreaker.png')}
                      alt="Check Outside Breaker Details"
                    />
                  </Item>
                </Grid>

                <Grid item xs={12}>
                  <Item className={classes.gridDetails}>
                    <Typography variant={'subtitle2'}>
                      {t('OUTSIDE_BREAKER')}
                    </Typography>
                    <Typography variant={'subtitle1'}>
                      {t('OUTSIDE_BREAKER_PARAH1')}
                    </Typography>
                    <Typography variant={'subtitle2'}>
                      {t('STEPS_TO_RESET_OUTSIDE_BREAKERS')}
                    </Typography>
                    <ol>
                      <li className={classes.textToBold}>
                        <Typography variant={'subtitle1'}>
                          {t('LOCATE_THE_OUTSIDE_BREAKER')}
                        </Typography>
                      </li>
                      <li className={classes.textToBold}>
                        <Typography variant={'subtitle1'}>
                          {t('ATTEMPT_TO_RESET_THE_OUTSIDE_BREAKER')}
                        </Typography>
                      </li>
                    </ol>
                  </Item>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      <Box className={classes.switchBoxDetails}>
        <Typography
          variant={'subtitle2'}
          className={classes.resetBreakerDetails}
        >
          {t('WERE_YOU_ABLE_TO_RESET_YOUR_BREAKER')}
        </Typography>
        <Box className={classes.switchDetails}>
          <Typography className={classes.switchOptions}>{t('NO')}</Typography>{' '}
          <OutageToggleSwitch
            inputProps={{ 'aria-label': 'Outage Toggle Switch' }}
          />
          <Typography className={classes.switchOptions}>{t('YES')}</Typography>{' '}
        </Box>
      </Box>

      <Box className={classes.buttonBox}>
        <Box className={classes.submitButtonWrapper}>
          <Button
            size={'large'}
            className={`${classes.resetBreakerButton}`}
            type="submit"
          >
            <Typography
              variant={'h4'}
              noWrap
              className={`${classes.outageButtontext}`}
              onClick={() => props.onReset()}
            >
              {t('RESETTING_THE_BREAKER_WORKED')}
            </Typography>
          </Button>
        </Box>
        <Box className={classes.submitButtonWrapper}>
          <Button
            size={'large'}
            className={`${classes.reportOutageButton}`}
            type="submit"
            onClick={() => props.onProceed()}
          >
            <Typography
              variant={'h4'}
              noWrap
              className={`${classes.outageButtontext}`}
            >
              {t('THIS_DID_NOT_WORK_REPORT_OUTAGE')}
            </Typography>
          </Button>
        </Box>
        <Box className={classes.backButton}>
          <Button
            className={classes.cancelButton}
            onClick={() => {
              props.onPrevious();
            }}
            disableRipple={true}
          >
            <Typography variant={'h4'} noWrap>
              {t('BACK')}
            </Typography>
          </Button>
        </Box>
      </Box>
    </form>
  );
}
